import { ComponentPropsWithoutRef, useEffect, useState } from "react";
import * as styles from "./SectionWalkthrough.css";
// @ts-ignore
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { MediaBackground } from "../components/MediaBackground";
import { ModalAlert } from "../modals/ModalAlert";
import { CardHeadingTextProps } from "../components/CardHeadingText";
import { ArrowLeft, ArrowRight, Check } from "@untitled-ui/icons-react";
import { MetaFunction, useNavigate } from "@remix-run/react";
import { soundModalComplete } from "~/lib/hooks/sounds/soundModalComplete";
import { useFormState } from "react-hook-form";
import { ButtonIcon } from "../elements/ButtonIcon";
import { Paragraph } from "../elements/Paragraph";
import { CardClass } from "../components/CardClass";
import { IMedia } from "~/types/IMedia";
import { Button } from "../elements/Button";

export type SectionWalkthroughProps = {
  steps: {
    media: IMedia;
    text: string;
    title: string;
  }[];
  open: boolean;
  defaultOpen?: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
} & ComponentPropsWithoutRef<"div">;

export const SectionWalkthrough = ({
  steps,
  open,
  defaultOpen = false,
  setOpen,
  ...props
}: SectionWalkthroughProps) => {
  gsap.registerPlugin(useGSAP);
  const completeTl = gsap.timeline({
    defaults: {
      duration: "0.3",
    },
  });

  const [currentStep, setCurrentStep] = useState(0);
  const isStepLast = currentStep === steps.length - 1;

  useEffect(() => {
    defaultOpen
      ? gsap.set("#background-complete", { autoAlpha: 1 })
      : gsap.set("#background-complete", { autoAlpha: 0 });
  }, []);

  useGSAP(() => {
    !open &&
      completeTl
        .to("#card", {
          scale: 0,
          ease: "elastic.in(1,1)",
          duration: "0.8",
        })
        .to("#background-complete", {
          autoAlpha: 0,
        })
        .to("#complete-video", {
          autoAlpha: "0",
          scale: "10",
          duration: "0.2",
        });

    open &&
      (setCurrentStep(0),
      completeTl
        .to(
          "#background-complete",

          {
            autoAlpha: 1,
          },
          "<"
        )
        .to(
          "#card",
          {
            scale: 1,
            ease: "elastic.out(1,1)",
            duration: "0.8",
          },
          "<"
        )
        .to(
          "#complete-video",
          {
            autoAlpha: "1",
            scale: "1",
            duration: "0.2",
          },
          "<"
        ));
  }, [open]);

  return (
    <>
      <div id="background-complete" className={styles.mediaBackground({defaultOpen: defaultOpen})}>
        <div className={styles.step}>
          <div></div>
          <div className={styles.card} id="card">
            <CardClass
              aspectRatio="shallow"
              media={steps[currentStep].media}
              text={steps[currentStep].text}
            >
              <Paragraph size="Xxl" className={styles.text}>
                {steps[currentStep].title}
              </Paragraph>
            </CardClass>
            <div className={styles.buttons}>
              {steps.length > 1 && (
                <Button
                  width="full"
                  size="sm"
                  type="button"
                  disabled={currentStep === 0}
                  isDisabled={currentStep === 0}
                  onClick={() => setCurrentStep(currentStep - 1)}
                  icon={ArrowLeft}
                  layout="left"
                >
                  <Paragraph>Back</Paragraph>
                </Button>
              )}
              <Button
                width="full"
                size="sm"
                type="submit"
                icon={isStepLast ? Check : ArrowRight}
                layout="right"
                onClick={() =>
                  isStepLast ? setOpen(false) : setCurrentStep(currentStep + 1)
                }
                // theme={isStepLast ? "colour" : "dark"}
                theme="dark"
              >
                <Paragraph>{isStepLast ? "Finish" : "Continue"}</Paragraph>
              </Button>
            </div>
          </div>
          <div className={styles.footer}>
            <div className={styles.steps}>
              <>
                {steps.map((_, index) => {
                  return (
                    <ButtonIcon
                      hasBackground
                      key={index}
                      disabled={currentStep < index}
                      // style={{ pointerEvents: "none" }}
                      onClick={() => setCurrentStep(index)}
                    >
                      <Paragraph size="Sm">{index + 1}</Paragraph>
                    </ButtonIcon>
                  );
                })}
              </>
            </div>
            <Button icon={ArrowRight} onClick={() => setOpen(false)}>
              <Paragraph>Skip</Paragraph>
            </Button>
          </div>
        </div>
        <div id="complete-video" className={styles.videoWrapper}>
          <MediaBackground
            className={styles.video}
            {...props}
            media={{
              video: {
                src: "https://customer-ji7awdm9ojrzi8s9.cloudflarestream.com/5744676703454473809ab7d7506cdfa2/manifest/video.m3u8",
                autoPlay: true,
                loop: true,
                muted: true,
                hasRef: false,
                hasPlayButton: false,
                // allowNavControl: false,
              },
              image: {
                src: "https://imagedelivery.net/5u-BnzY4d8R245ADRh1vnA/f6839eea-1360-45a7-909d-b79f67f7c200/public",
              },
            }}
          />
        </div>
      </div>
    </>
  );
};
